import React from 'react';
import { Provider } from './types';

interface ProviderCardProps {
  provider: Provider;
}

const ProviderCard: React.FC<ProviderCardProps> = ({ provider }) => {
  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, i) => (
      <i 
        key={i}
        className={`fas fa-star ${i < Math.floor(rating) ? 'filled' : ''}`}
        style={{ 
          color: i < Math.floor(rating) ? '#FFD700' : '#ddd',
          fontSize: '11px',
          marginRight: '1px'
        }}
      ></i>
    ));
  };

  const handleIconClick = (action: string) => {
    switch (action) {
      case 'website':
        if (provider.isVerified && provider.website) {
          window.open(provider.website, '_blank');
        }
        break;
      case 'phone':
        if (provider.phone) {
          window.location.href = `tel:${provider.phone}`;
        }
        break;
      case 'email':
        if (provider.email) {
          window.location.href = `mailto:${provider.email}`;
        }
        break;
      case 'whatsapp':
        if (provider.isVerified && provider.phone) {
          const whatsappUrl = `https://wa.me/${provider.phone.replace(/\D/g, '')}`;
          window.open(whatsappUrl, '_blank');
        }
        break;
      case 'store':
        if (provider.isPremium) {
          // Navegar a la página de la tienda del proveedor
          console.log('Navegando a la tienda del proveedor:', provider.name);
        }
        break;
    }
  };

  return (
    <div className="provider-card-container" style={{ 
      width: 'calc(25% - 15px)', 
      marginBottom: '15px',
      display: 'inline-block',
      verticalAlign: 'top',
      marginRight: '10px'
    }}>
      <div className="provider-card" style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        border: '1px solid #e5e5e5',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '12px',
        height: '240px',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        overflow: 'hidden'
      }}>
        {/* Header con icono y nombre */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '10px',
          paddingBottom: '8px',
          borderBottom: '1px solid #EEEEEE'
        }}>
          <div style={{
            backgroundColor: '#3498DB',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '6px',
            flexShrink: 0
          }}>
            <i className="fas fa-hard-hat" style={{ color: 'white', fontSize: '12px' }}></i>
          </div>
          <h3 style={{
            margin: 0,
            fontSize: '12px',
            fontWeight: '600',
            color: '#333333',
            lineHeight: '1.2',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {provider.name}
          </h3>
        </div>

        {/* Información del proveedor */}
        <div style={{ flex: 1, marginBottom: '8px', overflow: 'hidden' }}>
          {/* Especialidad */}
          <div style={{ marginBottom: '6px' }}>
            <span style={{ fontWeight: 'bold', fontSize: '10px', color: '#333333' }}>
              Especialidad:
            </span>
            <div style={{ 
              fontSize: '10px', 
              color: '#333333', 
              lineHeight: '1.3',
              marginTop: '2px',
              maxHeight: '28px',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}>
              {provider.specialties?.join(', ') || 'No especificada'}
            </div>
          </div>

          {/* Rating y estrellas */}
          <div style={{ marginBottom: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
              {renderStars(provider.rating)}
              <span style={{ 
                fontSize: '9px', 
                color: '#666666', 
                marginLeft: '3px'
              }}>
                {provider.rating}/5
              </span>
            </div>
            <span style={{ 
              fontSize: '9px', 
              color: '#666666'
            }}>
              ({provider.reviewCount} reseñas)
            </span>
          </div>

          {/* Estado de verificación y premium */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '6px', flexWrap: 'wrap' }}>
            {provider.isVerified && (
              <span style={{
                backgroundColor: '#20D3C7',
                color: 'white',
                fontSize: '8px',
                padding: '1px 4px',
                borderRadius: '6px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '1px'
              }}>
                <i className="fas fa-check" style={{ fontSize: '7px' }}></i>
                Verificado
              </span>
            )}
            {provider.isPremium && (
              <span style={{
                backgroundColor: '#FFD700',
                color: 'white',
                fontSize: '8px',
                padding: '1px 4px',
                borderRadius: '6px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '1px'
              }}>
                <i className="fas fa-star" style={{ fontSize: '7px' }}></i>
                Premium
              </span>
            )}
          </div>
        </div>

        {/* Separador */}
        <div style={{
          borderTop: '1px solid #EEEEEE',
          marginBottom: '8px'
        }}></div>

        {/* Iconos de acción */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '4px'
        }}>
          {/* Tienda */}
          <button
            onClick={() => handleIconClick('store')}
            disabled={!provider.isPremium}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: provider.isPremium ? '#2ECC71' : '#E0E0E0',
              color: provider.isPremium ? 'white' : '#999999',
              cursor: provider.isPremium ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            title={provider.isPremium ? 'Ver tienda' : 'Tienda solo para usuarios premium'}
          >
            <i className="fas fa-store" style={{ fontSize: '10px' }}></i>
          </button>

          {/* Email */}
          <button
            onClick={() => handleIconClick('email')}
            disabled={!provider.email}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: provider.email ? '#F0F0F0' : '#E0E0E0',
              color: provider.email ? '#333333' : '#999999',
              cursor: provider.email ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            title={provider.email ? 'Enviar email' : 'Email no disponible'}
          >
            <i className="fas fa-envelope" style={{ fontSize: '10px' }}></i>
          </button>

          {/* Teléfono */}
          <button
            onClick={() => handleIconClick('phone')}
            disabled={!provider.phone}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: provider.phone ? '#F0F0F0' : '#E0E0E0',
              color: provider.phone ? '#333333' : '#999999',
              cursor: provider.phone ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            title={provider.phone ? 'Llamar' : 'Teléfono no disponible'}
          >
            <i className="fas fa-phone" style={{ fontSize: '10px' }}></i>
          </button>

          {/* Website */}
          <button
            onClick={() => handleIconClick('website')}
            disabled={!provider.isVerified || !provider.website}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: provider.isVerified && provider.website ? '#F0F0F0' : '#E0E0E0',
              color: provider.isVerified && provider.website ? '#333333' : '#999999',
              cursor: provider.isVerified && provider.website ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            title={provider.isVerified && provider.website ? 'Visitar sitio web' : 'Sitio web no disponible'}
          >
            <i className="fas fa-globe" style={{ fontSize: '10px' }}></i>
          </button>

          {/* WhatsApp */}
          <button
            onClick={() => handleIconClick('whatsapp')}
            disabled={!provider.isVerified || !provider.phone}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: provider.isVerified && provider.phone ? '#25D366' : '#E0E0E0',
              color: provider.isVerified && provider.phone ? 'white' : '#999999',
              cursor: provider.isVerified && provider.phone ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            title={provider.isVerified && provider.phone ? 'Contactar por WhatsApp' : 'WhatsApp solo para proveedores verificados'}
          >
            <i className="fab fa-whatsapp" style={{ fontSize: '10px' }}></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProviderCard;
